/**
 * Block Examples Library v2 capture core — PURE data-in/data-out.
 *
 * Produces a compact semantic blueprint for adaptation plus full render-only
 * preview screens. This module has no Firebase dependency and is shared by
 * browser capture and CLI/admin fixture tooling.
 */

import type {
  AiScreenMeta,
  BlockContract,
  BlockDataUsage,
  SimplifiedFunnelVariable,
  SimplifiedV2StepNode,
  StrategyBlockInstruction,
  StrategyStepNode,
} from '@segmently/schema-models/simplified';
import {
  BLOCK_LIBRARY_SCHEMA_VERSION,
  canonicalizeBlockLibraryValue,
  computeBlockLibrarySourceChecksum,
  estimateBlockLibraryPayloadBytes,
  estimateBlockLibraryValueBytes,
  validateBlockLibrarySimplifiedScreen,
} from '../../shared-types/src/blockLibrary.types';
import type {
  BlockLibraryAssetExample,
  BlockLibraryBlueprint,
  BlockLibraryContractSummary,
  BlockLibraryExampleInput,
  BlockLibraryScopeMetadata,
  BlockLibrarySourceRef,
} from '../../shared-types/src/blockLibrary.types';
import { simplifiedV2ScreenAdapter } from '../../screen-utils/services/SimplifiedV2ScreenAdapter';

export type BlockLibraryCaptureErrorCode = 'no_screens' | 'projection_failed';

export class BlockLibraryCaptureError extends Error {
  constructor(
    readonly code: BlockLibraryCaptureErrorCode,
    message: string,
    readonly screenIds: string[] = [],
  ) {
    super(message);
    this.name = 'BlockLibraryCaptureError';
  }
}

export interface BlockLibraryCaptureSummary {
  screens: number;
  manualScreens: number;
  contractInputs: number;
  contractOutputs: number;
  instructions: number;
  evidenceReferences: number;
  assetIntents: number;
  variables: string[];
  blueprintBytes: number;
  previewBytes: number;
  payloadBytes: number;
  assetExamples: number;
  hasAssetPlans: boolean;
}

/** What save/CLI authoring needs: both representations, checksum and defaults. */
export interface BlockLibraryCapture {
  blueprint: BlockLibraryBlueprint;
  previewScreens: StrategyStepNode[];
  sourceChecksum: string;
  summary: BlockLibraryCaptureSummary;
  defaults: Pick<
    BlockLibraryExampleInput,
    'schemaVersion' | 'scopeMetadata' | 'screensCount' | 'contractSummary' | 'sourceRef'
  > & { name: string };
}

export interface BlockLibraryCaptureSource {
  projectId: string;
  strategyId: string;
  blockId: string;
  /** Strategy doc — variable definitions are narrowed to a block closure. */
  strategy: Record<string, unknown>;
  /** Full block doc. */
  block: Record<string, unknown>;
  /** Full StrategyStepNode docs of the block. */
  screens: Array<Record<string, unknown>>;
  /** Locale used when a manual full screen must be simplified. */
  locale?: string;
}

/**
 * Build a v2 capture. AI-created screens reuse their stored `aiContent`;
 * manual screens are projected with the canonical SimplifiedV2 adapter. Both
 * paths preserve semantic aiMeta but exclude generated/provider asset state.
 */
export function buildBlockLibraryCaptureFromData(source: BlockLibraryCaptureSource): BlockLibraryCapture {
  const { strategyId, blockId, strategy, block } = source;
  const locale = source.locale ?? 'en-US';
  const sourceScreens = source.screens
    .filter((screen): screen is Record<string, unknown> => isRecord(screen))
    .map((screen) => ({ ...screen }))
    .sort((left, right) => {
      const leftOrder = typeof left.order === 'number' ? left.order : Number.MAX_SAFE_INTEGER;
      const rightOrder = typeof right.order === 'number' ? right.order : Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder
        || String(left.id ?? '').localeCompare(String(right.id ?? ''));
    });
  if (sourceScreens.length === 0) {
    throw new BlockLibraryCaptureError('no_screens', 'A library example must contain at least one screen.');
  }

  const previewScreens = sourceScreens.map((screen) => cloneJson(screen)) as unknown as StrategyStepNode[];
  const projectionFailures: string[] = [];
  const projectionFailureDetails: string[] = [];
  const simplifiedScreens = sourceScreens.map((screen, index) => {
    const screenId = nonEmptyString(screen.id) ?? `screen-index-${index}`;
    try {
      return projectScreenForBlueprint(screen, locale);
    } catch (error) {
      projectionFailures.push(screenId);
      projectionFailureDetails.push(
        `${screenId}: ${error instanceof Error ? error.message : 'unknown projection failure'}`,
      );
      return null;
    }
  }).filter((screen): screen is SimplifiedV2StepNode => Boolean(screen));

  if (projectionFailures.length > 0 || simplifiedScreens.length !== previewScreens.length) {
    const ids = projectionFailures.length > 0 ? projectionFailures : ['unknown'];
    throw new BlockLibraryCaptureError(
      'projection_failed',
      `Cannot derive simplified library screens: ${ids.join(', ')}. ${projectionFailureDetails.join(' | ')}`.trim(),
      ids,
    );
  }

  const screenIds = new Set(simplifiedScreens.map((screen) => screen.id));
  const closure = buildVariableClosure(strategy.variables, block, sourceScreens, screenIds);
  const contract = isRecord(block.blockContract)
    ? stripSourceTaskScope(block.blockContract) as unknown as BlockContract
    : {};
  const instructions = Array.isArray(block.instructions)
    ? stripSourceTaskScope(block.instructions) as unknown as StrategyBlockInstruction[]
    : [];
  const blockDataUsage = isRecord(block.dataUsage)
    ? stripSourceTaskScope(block.dataUsage) as unknown as BlockDataUsage
    : undefined;

  const blockType = nonEmptyString(block.blockType) ?? 'unknown';
  const customSubtype = nonEmptyString(block.customSubtype);
  const scopeMetadata: BlockLibraryScopeMetadata = {
    blockType,
    ...(customSubtype ? { customSubtype } : {}),
  };
  const firstScreenId = nonEmptyString(block.firstScreenId) ?? simplifiedScreens[0].id;

  const blueprint: BlockLibraryBlueprint = {
    strategicBlock: {
      id: nonEmptyString(block.id) ?? blockId,
      ...(nonEmptyString(block.entityId) ? { entityId: nonEmptyString(block.entityId) } : {}),
      name: nonEmptyString(block.name) ?? blockId,
      ...(nonEmptyString(block.blockName) ? { blockName: nonEmptyString(block.blockName) } : {}),
      blockType: blockType as never,
      ...(customSubtype ? { customSubtype } : {}),
      ...(nonEmptyString(block.description) ? { description: nonEmptyString(block.description) } : {}),
      firstScreenId,
      ...(Array.isArray(block.functionMapping)
        ? { functionMapping: cloneJson(block.functionMapping) as string[] }
        : {}),
      ...(nonEmptyString(block.additionalInfo) ? { additionalInfo: nonEmptyString(block.additionalInfo) } : {}),
    },
    screens: simplifiedScreens,
    instructions,
    contract,
    dataSemantics: {
      ...(blockDataUsage ? { blockDataUsage } : {}),
      screens: simplifiedScreens.map((screen) => ({
        screenId: screen.id,
        dataMapping: cloneJson(screen.aiMeta?.dataMapping ?? []),
        variableRealizations: cloneJson(screen.aiMeta?.variableRealizations ?? []),
      })),
    },
    assetExamples: collectAssetExamples(sourceScreens),
    strategyVariables: closure as Record<string, SimplifiedFunnelVariable>,
  };

  const sourceRef: BlockLibrarySourceRef = {
    strategyId,
    blockId,
    blockEntityId: nonEmptyString(block.entityId) ?? blockId,
  };
  const sourceChecksum = computeBlockLibrarySourceChecksum({
    schemaVersion: BLOCK_LIBRARY_SCHEMA_VERSION,
    scopeMetadata,
    blueprint,
    previewScreens,
    sourceRef,
  });
  const contractInputs = contract.inputVariables?.length ?? 0;
  const contractOutputs = contract.outputAssignment?.length ?? 0;
  const evidenceReferences = new Set<string>();
  for (const item of blockDataUsage?.items ?? []) {
    const sourcePath = nonEmptyString(item.sourcePath);
    if (sourcePath) evidenceReferences.add(sourcePath);
  }
  for (const screen of simplifiedScreens) {
    for (const item of screen.aiMeta?.dataMapping ?? []) {
      const sourcePath = nonEmptyString(item.sourcePath);
      if (sourcePath) evidenceReferences.add(sourcePath);
    }
  }
  const assetIntents = simplifiedScreens.reduce(
    (count, screen) => count + (screen.aiMeta?.assetIntents?.length ?? 0),
    0,
  );
  const contractSummary: BlockLibraryContractSummary = {
    inputs: contractInputs,
    outputs: contractOutputs,
  };

  return {
    blueprint,
    previewScreens,
    sourceChecksum,
    summary: {
      screens: simplifiedScreens.length,
      manualScreens: sourceScreens.filter((screen) => !isRecord(screen.aiContent)).length,
      contractInputs,
      contractOutputs,
      instructions: instructions.length,
      evidenceReferences: evidenceReferences.size,
      assetIntents,
      variables: Object.keys(closure).sort(),
      blueprintBytes: estimateBlockLibraryValueBytes(blueprint),
      previewBytes: estimateBlockLibraryValueBytes(previewScreens),
      payloadBytes: estimateBlockLibraryPayloadBytes({ blueprint, previewScreens }),
      assetExamples: blueprint.assetExamples.length,
      hasAssetPlans: simplifiedScreens.some((screen) => (screen.aiMeta?.assetIntents?.length ?? 0) > 0)
        || blueprint.assetExamples.length > 0,
    },
    defaults: {
      schemaVersion: BLOCK_LIBRARY_SCHEMA_VERSION,
      name: nonEmptyString(block.name) ?? blockId,
      scopeMetadata,
      screensCount: simplifiedScreens.length,
      contractSummary,
      sourceRef,
    },
  };
}

function projectScreenForBlueprint(screen: Record<string, unknown>, locale: string): SimplifiedV2StepNode {
  const id = nonEmptyString(screen.id);
  const screenType = nonEmptyString(screen.screenType);
  if (!id || !screenType) throw new Error('Screen id and screenType are required.');

  const aiContent = isRecord(screen.aiContent) ? sanitizeBlueprintValue(screen.aiContent) : undefined;
  const base = aiContent
    ? {
      id,
      name: nonEmptyString(screen.name),
      screenType: screenType as never,
      content: aiContent as never,
      ...(isRecord(screen.position) ? { position: cloneJson(screen.position) as never } : {}),
    }
    : simplifiedV2ScreenAdapter.toSimplified(screen as never, locale);
  const aiMeta = sanitizeAiMeta(screen.aiMeta);
  const simplified: SimplifiedV2StepNode = {
    ...base,
    id,
    screenType: screenType as never,
    content: sanitizeBlueprintValue(base.content) as never,
    // Navigation is semantic strategy context, not render-only state. Keep it
    // in the simplified blueprint so adaptation/materialization can validate
    // and rewrite routes without reading previewScreens.
    ...(isRecord(screen.action)
      ? { action: sanitizeBlueprintValue(screen.action) as never }
      : {}),
    ...(aiMeta ? { aiMeta } : {}),
  };

  const simplifiedIssues = validateBlockLibrarySimplifiedScreen(simplified);
  if (simplifiedIssues.length > 0) {
    throw new Error(simplifiedIssues.map((issue) => (
      `${issue.path || 'screen'} ${issue.message}`
    )).join('; '));
  }

  // Prove the simplified screen can be re-materialized with its full preview
  // template AND projected back without losing semantic content. Identity-only
  // checks are insufficient because the adapter deliberately overwrites id/type.
  const projected = simplifiedV2ScreenAdapter.toFullStepNode({
    simplified,
    lang: locale,
    template: screen as never,
  });
  if (projected.id !== id || String(projected.screenType) !== screenType) {
    throw new Error('Simplified/full projection identity mismatch.');
  }
  const roundTrip = simplifiedV2ScreenAdapter.toSimplified(projected, locale);
  if (canonicalizeBlockLibraryValue(normalizeTapTargetAliases(sanitizeBlueprintValue(roundTrip.content)))
    !== canonicalizeBlockLibraryValue(normalizeTapTargetAliases(sanitizeBlueprintValue(simplified.content)))) {
    throw new Error('Simplified/full projection loses semantic content.');
  }
  return cloneJson(simplified);
}

function normalizeTapTargetAliases(value: unknown): unknown {
  if (!isRecord(value)) return value;
  const normalized = cloneJson(value) as Record<string, unknown>;
  const targets: unknown[] = [];
  if (isRecord(normalized.header)) {
    targets.push(normalized.header.skip, normalized.header.back, normalized.header.close);
  }
  if (isRecord(normalized.actionBar)) {
    targets.push(normalized.actionBar.primary, normalized.actionBar.secondary);
  }
  if (isRecord(normalized.paywall)) {
    targets.push(normalized.paywall.purchaseAction, normalized.paywall.closeButton);
  }
  for (const candidate of targets) {
    if (!isRecord(candidate)) continue;
    const label = nonEmptyString(candidate.label) ?? nonEmptyString(candidate.content);
    if (label) candidate.label = label;
    delete candidate.content;
  }
  return normalized;
}

function sanitizeAiMeta(value: unknown): AiScreenMeta | undefined {
  if (!isRecord(value)) return undefined;
  const semantic = {
    ...(Array.isArray(value.dataMapping) ? { dataMapping: stripSourceTaskScope(value.dataMapping) } : {}),
    ...(nonEmptyString(value.blockRole) ? { blockRole: nonEmptyString(value.blockRole) } : {}),
    ...(isRecord(value.motivationSpec) ? { motivationSpec: stripSourceTaskScope(value.motivationSpec) } : {}),
    ...(Array.isArray(value.assetIntents) ? { assetIntents: stripSourceTaskScope(value.assetIntents) } : {}),
    ...(Array.isArray(value.variableRealizations)
      ? { variableRealizations: stripSourceTaskScope(value.variableRealizations) }
      : {}),
  };
  return Object.keys(semantic).length > 0 ? semantic as AiScreenMeta : undefined;
}

function sanitizeBlueprintValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sanitizeBlueprintValue).filter((entry) => entry !== undefined);
  }
  if (!isRecord(value)) return value;
  const forbidden = new Set([
    'aiAssets', 'generatedUrl', 'posterUrl', 'referenceImages', 'provider', 'model',
    'generationModel', 'generationTimestamp', 'sourceDataSnapshot',
  ]);
  const result: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (forbidden.has(key)) continue;
    const sanitized = sanitizeBlueprintValue(child);
    if (isRecord(sanitized) && Object.keys(sanitized).length === 0) continue;
    if (Array.isArray(sanitized) && sanitized.length === 0) {
      result[key] = sanitized;
      continue;
    }
    if (sanitized !== undefined) result[key] = sanitized;
  }
  return result;
}

function collectAssetExamples(screens: Array<Record<string, unknown>>): BlockLibraryAssetExample[] {
  const examples: BlockLibraryAssetExample[] = [];
  for (const screen of screens) {
    const screenId = nonEmptyString(screen.id);
    if (!screenId || !isRecord(screen.aiAssets)) continue;
    for (const [slotKey, slot] of Object.entries(screen.aiAssets)) {
      if (!isRecord(slot) || !isRecord(slot.experiments)) continue;
      const selectedId = nonEmptyString(slot.selectedExperimentId);
      const experimentEntries = Object.entries(slot.experiments)
        .filter((entry): entry is [string, Record<string, unknown>] => isRecord(entry[1]));
      const selected = selectedId
        ? experimentEntries.find(([experimentId]) => experimentId === selectedId)
        : experimentEntries[0];
      if (!selected) continue;
      const [experimentId, experiment] = selected;
      const assetIntent = findAssetIntentPattern(screen.aiMeta, slotKey);
      const promptTemplate = buildCompactAssetPattern(
        assetIntent,
        nonEmptyString(experiment.promptTemplate),
      );
      const negativePrompt = compactNegativePattern(nonEmptyString(experiment.negativePrompt));
      const style = nonEmptyString(experiment.style);
      const aspectRatio = nonEmptyString(experiment.aspectRatio);
      const assetType = experiment.assetType === 'video' ? 'video' : 'image';
      if (!promptTemplate && !negativePrompt && !style && !aspectRatio) continue;
      examples.push({
        screenId,
        slotKey,
        experimentId,
        ...(promptTemplate ? { promptTemplate } : {}),
        ...(negativePrompt ? { negativePrompt } : {}),
        ...(style ? { style } : {}),
        ...(aspectRatio ? { aspectRatio } : {}),
        assetType,
      });
    }
  }
  return examples.sort((left, right) => (
    left.screenId.localeCompare(right.screenId)
    || left.slotKey.localeCompare(right.slotKey)
    || String(left.experimentId ?? '').localeCompare(String(right.experimentId ?? ''))
  ));
}

function findAssetIntentPattern(aiMeta: unknown, slotKey: string): Record<string, unknown> | undefined {
  if (!isRecord(aiMeta) || !Array.isArray(aiMeta.assetIntents)) return undefined;
  const candidates = aiMeta.assetIntents.filter(isRecord);
  return candidates.find((intent) => (
    nonEmptyString(intent.slotKey) === slotKey || nonEmptyString(intent.slotRole) === slotKey
  )) ?? (candidates.length === 1 ? candidates[0] : undefined);
}

function buildCompactAssetPattern(
  intent: Record<string, unknown> | undefined,
  rawPrompt: string | undefined,
): string | undefined {
  if (intent) {
    const parts = [
      nonEmptyString(intent.designFunction) ? `design function: ${nonEmptyString(intent.designFunction)}` : undefined,
      nonEmptyString(intent.visualJob) ? `visual job: ${nonEmptyString(intent.visualJob)}` : undefined,
      nonEmptyString(intent.supportsContentPath)
        ? `supports: ${nonEmptyString(intent.supportsContentPath)}`
        : undefined,
      nonEmptyString(intent.slotRole) ? `slot role: ${nonEmptyString(intent.slotRole)}` : undefined,
    ].filter(Boolean);
    if (parts.length > 0) return parts.join('; ').slice(0, 1_200);
  }
  if (!rawPrompt) return undefined;
  // Older/manual assets may not have a semantic intent yet. Keep only a short
  // composition/style excerpt and never the provider-ready source package.
  const labelled = ['Subject:', 'Action/mechanism:', 'Composition:', 'Style:']
    .map((label) => extractPromptLabel(rawPrompt, label))
    .filter(Boolean)
    .join('; ');
  const fallback = labelled || firstSentences(rawPrompt, 2);
  return fallback
    .replace(/(?:https?:\/\/|gs:\/\/)[^\s]+/gi, '[external reference omitted]')
    .replace(/@[a-zA-Z]+\[[^\]]+\](?:\.[^\s;,.]+)?/g, '[source reference omitted]')
    .slice(0, 1_200)
    .trim() || undefined;
}

function extractPromptLabel(prompt: string, label: string): string | undefined {
  const start = prompt.indexOf(label);
  if (start < 0) return undefined;
  const valueStart = start + label.length;
  const nextLabel = prompt.slice(valueStart).search(/\b(?:Subject|Action\/mechanism|Setting\/background|Composition|Style|Exclusions):/);
  const raw = nextLabel >= 0
    ? prompt.slice(valueStart, valueStart + nextLabel)
    : prompt.slice(valueStart);
  const compact = raw.replace(/\s+/g, ' ').trim();
  return compact ? `${label.slice(0, -1).toLowerCase()}: ${compact.slice(0, 300)}` : undefined;
}

function compactNegativePattern(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return firstSentences(value, 4).slice(0, 800).trim() || undefined;
}

function firstSentences(value: string, count: number): string {
  return (value.match(/[^.!?]+[.!?]+|[^.!?]+$/g) ?? [value])
    .slice(0, count)
    .map((sentence) => sentence.trim())
    .join(' ');
}

function buildVariableClosure(
  rawVariables: unknown,
  block: Record<string, unknown>,
  sourceScreens: Array<Record<string, unknown>>,
  screenIds: Set<string>,
): Record<string, unknown> {
  const allVariables = isRecord(rawVariables) ? rawVariables : {};
  const contract = isRecord(block.blockContract) ? block.blockContract : {};
  const blockEntityId = nonEmptyString(block.entityId) ?? '';
  const toBlockScreenId = (rawId: string): string | undefined => {
    if (screenIds.has(rawId)) return rawId;
    if (blockEntityId && rawId.startsWith(`${blockEntityId}__`)) {
      const plain = rawId.slice(blockEntityId.length + 2);
      if (screenIds.has(plain)) return plain;
    }
    return undefined;
  };
  const normalizeDefinition = (definition: Record<string, unknown>): Record<string, unknown> => {
    const normalized = stripSourceTaskScope(definition) as Record<string, unknown>;
    if (Array.isArray(definition.boundScreenIds)) {
      normalized.boundScreenIds = Array.from(new Set(
        definition.boundScreenIds.map(String).map(toBlockScreenId).filter(Boolean) as string[],
      ));
    }
    if (isRecord(definition.screenBindings)) {
      const rekeyed: Record<string, unknown> = {};
      for (const [rawId, binding] of Object.entries(definition.screenBindings)) {
        const plain = toBlockScreenId(rawId);
        if (plain) rekeyed[plain] = stripSourceTaskScope(binding);
      }
      normalized.screenBindings = rekeyed;
    }
    return normalized;
  };

  const wanted = new Set<string>();
  const inputs = Array.isArray(contract.inputVariables) ? contract.inputVariables : [];
  const outputs = Array.isArray(contract.outputAssignment) ? contract.outputAssignment : [];
  for (const definition of [...inputs, ...outputs]) {
    if (!isRecord(definition)) continue;
    const variableId = nonEmptyString(definition.id);
    if (variableId) wanted.add(variableId);
  }
  const screensJson = JSON.stringify(sourceScreens);
  for (const [variableId, definition] of Object.entries(allVariables)) {
    if (!isRecord(definition)) continue;
    const boundIds = Array.isArray(definition.boundScreenIds) ? definition.boundScreenIds.map(String) : [];
    const bindingIds = isRecord(definition.screenBindings) ? Object.keys(definition.screenBindings) : [];
    if (boundIds.some((id) => toBlockScreenId(id) !== undefined)
      || bindingIds.some((id) => toBlockScreenId(id) !== undefined)
      || screensJson.includes(`\"${variableId}\"`)
      || screensJson.includes(`@${variableId}`)) {
      wanted.add(variableId);
    }
  }

  const closure: Record<string, unknown> = {};
  for (const variableId of Array.from(wanted).sort()) {
    const definition = allVariables[variableId];
    if (isRecord(definition)) closure[variableId] = normalizeDefinition(definition);
  }
  return closure;
}

function stripSourceTaskScope<T>(value: T): T {
  if (Array.isArray(value)) return value.map(stripSourceTaskScope) as T;
  if (!isRecord(value)) return cloneJson(value);
  const result: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (key === 'taskId' || key === 'taskIds') continue;
    result[key] = stripSourceTaskScope(child);
  }
  return result as T;
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
